import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useStoreAuth } from '../../contexts/StoreAuthContext';
import { storeInventoryAPI, storeProductsAPI } from '../../services/storeAPI';
import { useDebouncedSearch } from '../../hooks/useDebouncedSearch';
import StoreHeader from '../../components/Layout/StoreHeader';
import { WeightPoolCell } from './WeightPoolCell';

interface InventoryItem {
  id: number;
  product_id: number;
  product_name: string;
  product_sku: string;
  product_upc: string | null;
  category: string;
  category_display: string;
  form: string;
  form_display: string;
  size_lbs: number;
  wholesale_price: number;
  retail_price: number;
  quantity: number;
  // weight_tracked products track a POUND POOL (quantity_lbs), not the unit count.
  weight_tracked?: boolean;
  quantity_lbs?: number;
  location: string;
  reorder_threshold: number;
  needs_reorder: boolean;
  is_active: boolean;
}

interface Product {
  id: number;
  name: string;
  sku: string;
  upc: string | null;
  category: string;
  category_display: string;
  form: string;
  form_display: string;
  size_lbs: number;
  wholesale_price: number;
  retail_price: number;
  is_active: boolean;
  // So the pound control renders even before a StoreInventory row exists.
  weight_tracked?: boolean;
}

interface EditingCell {
  itemId: number;      // StoreInventory row id, 0 when the product has no row yet
  quantity: number;
  productId: number;   // StoreProduct id — used to CREATE the row when itemId === 0
}

// Product categories in display order
const CATEGORIES = [
  { value: 'LAYER', label: 'Layer' },
  { value: 'ALL_FLOCK', label: 'All Flock' },
  { value: 'STARTER', label: 'Starter' },
  { value: 'GROWER', label: 'Grower' },
  { value: 'SCRATCH', label: 'Scratch' },
  { value: 'PIG', label: 'Swine' },
  { value: 'RABBIT', label: 'Rabbit' },
  { value: 'CATTLE', label: 'Sheep & Goat' },
  { value: 'ALL_STOCK', label: 'All Stock' },
  { value: 'HORSE', label: 'Equine' },
  { value: 'OTHER', label: 'Other' },
];

const StoreInventoryList: React.FC = () => {
  const { isAuthenticated, user } = useStoreAuth();
  const navigate = useNavigate();

  const [inventory, setInventory] = useState<InventoryItem[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [showLowOnly, setShowLowOnly] = useState(false);
  const [editingCell, setEditingCell] = useState<EditingCell | null>(null);
  const [savingId, setSavingId] = useState<number | null>(null);
  // Client-side search: the input binds to `searchText` (instant, never blocks); the
  // grouped list below filters on the debounced value so typing doesn't re-run the
  // heavy group/render on every keystroke. 250ms is right for an in-memory filter.
  const { value: searchText, setValue: setSearchText, debounced: debouncedSearch } =
    useDebouncedSearch('', 250);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/store/login');
      return;
    }
    loadData();
  }, [isAuthenticated, navigate]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');
      const [inventoryData, productsData] = await Promise.all([
        storeInventoryAPI.getAll(),
        storeProductsAPI.getAll(),
      ]);
      setInventory(inventoryData);
      setProducts(productsData);
    } catch (err: any) {
      setError(err.message || 'Failed to load inventory');
    } finally {
      setLoading(false);
    }
  };

  // Build the grouped, filtered list. Keyed on the DEBOUNCED search (not the instant
  // input) so a keystroke doesn't re-run this or rebuild the table — the input stays
  // responsive. Filters combine as AND: category AND low-stock AND text(name/SKU/UPC).
  // Grouping happens AFTER filtering, so a category with no matches produces no key —
  // empty group headers never render.
  const groupedData = useMemo(() => {
    const inventoryMap = new Map(inventory.map(i => [i.product_id, i]));

    // Include all products, adding inventory if it exists
    const allItems = products.map(product => {
      const invItem = inventoryMap.get(product.id);
      return {
        product,
        inventory: invItem,
        quantity: invItem?.quantity ?? 0,
        // Prefer the inventory row's flag; fall back to the product's so a
        // weight_tracked product with NO row yet still shows the pound control.
        weightTracked: invItem?.weight_tracked ?? product.weight_tracked ?? false,
        quantityLbs: invItem?.quantity_lbs ?? 0,
        needsReorder: invItem?.needs_reorder ?? false,
        reorderThreshold: invItem?.reorder_threshold ?? 10,
        id: invItem?.id ?? 0,
      };
    });

    // Filter by category
    let filtered = categoryFilter === 'all'
      ? allItems
      : allItems.filter(item => item.product.category === categoryFilter);

    // Filter by low stock
    if (showLowOnly) {
      filtered = filtered.filter(item => item.needsReorder || item.quantity <= item.reorderThreshold);
    }

    // Filter by search text — matches product name, SKU, or UPC
    const q = debouncedSearch.trim().toLowerCase();
    if (q) {
      filtered = filtered.filter(item =>
        item.product.name.toLowerCase().includes(q) ||
        (item.product.sku || '').toLowerCase().includes(q) ||
        (item.product.upc || '').toLowerCase().includes(q)
      );
    }

    // Group by category
    const grouped: { [key: string]: typeof filtered } = {};
    filtered.forEach(item => {
      const cat = item.product.category;
      if (!grouped[cat]) grouped[cat] = [];
      grouped[cat].push(item);
    });

    return grouped;
  }, [inventory, products, categoryFilter, showLowOnly, debouncedSearch]);

  // Merge ONE inventory row into local state in place — patch the existing row
  // (matched by product_id) or append a fresh one built from the product record.
  // Used instead of loadData() so a +/−/set touches only that row: no full-list
  // refetch, no re-render churn, and the scroll position is preserved.
  const upsertInventoryRow = (productId: number, patch: Partial<InventoryItem>) => {
    setInventory(prev => {
      if (prev.some(r => r.product_id === productId)) {
        return prev.map(r => r.product_id === productId ? { ...r, ...patch } : r);
      }
      const product = products.find(p => p.id === productId);
      if (!product) return prev; // every row derives from a product; guard anyway
      return [...prev, {
        id: 0,
        product_id: product.id,
        product_name: product.name,
        product_sku: product.sku,
        product_upc: product.upc,
        category: product.category,
        category_display: product.category_display,
        form: product.form,
        form_display: product.form_display,
        size_lbs: product.size_lbs,
        wholesale_price: product.wholesale_price,
        retail_price: product.retail_price,
        quantity: 0,
        location: 'FLOOR',
        reorder_threshold: 10,
        needs_reorder: false,
        is_active: product.is_active,
        ...patch,
      }];
    });
  };

  const handleQuickAdjust = async (itemId: number, delta: number, productId: number) => {
    const prevInventory = inventory; // snapshot for revert-on-error
    setSavingId(productId);
    try {
      if (!itemId) {
        // No StoreInventory row yet — create it. Only "+" reaches here (the "−"
        // button is disabled at 0), so the initial count is the positive delta.
        const qty = Math.max(0, delta);
        upsertInventoryRow(productId, { quantity: qty, needs_reorder: qty <= 10 }); // optimistic
        const res = await storeInventoryAPI.create({ product_id: productId, quantity: qty });
        // Reconcile from the server row and carry its REAL id, so the next tap
        // adjusts this row instead of creating a duplicate.
        upsertInventoryRow(productId, {
          id: res.id,
          quantity: res.quantity ?? qty,
          needs_reorder: res.needs_reorder ?? qty <= 10,
          reorder_threshold: res.reorder_threshold ?? 10,
        });
        setSuccess('Stock set');
      } else {
        // Existing row — optimistic bump, then reconcile to the server quantity.
        setInventory(prev => prev.map(r => r.id === itemId ? { ...r, quantity: r.quantity + delta } : r));
        const res = await storeInventoryAPI.adjust(itemId, delta, 'QUICK_ADJUST', `Quick ${delta > 0 ? '+' : ''}${delta}`);
        setInventory(prev => prev.map(r => r.id === itemId
          ? { ...r, quantity: res.new_quantity, needs_reorder: res.needs_reorder } : r));
        setSuccess(`Quantity ${delta > 0 ? 'increased' : 'decreased'}`);
      }
      setTimeout(() => setSuccess(''), 2000);
    } catch (err: any) {
      setInventory(prevInventory); // revert the optimistic change — never show a wrong number
      setError(err.message || 'Failed to adjust');
    } finally {
      setSavingId(null);
    }
  };

  const handleEditStart = (itemId: number, currentQty: number, productId: number) => {
    setEditingCell({ itemId, quantity: currentQty, productId });
  };

  const handleEditChange = (value: string) => {
    if (!editingCell) return;
    const qty = parseInt(value) || 0;
    setEditingCell({ ...editingCell, quantity: Math.max(0, qty) });
  };

  const handleEditSave = async () => {
    if (!editingCell) return;
    const { itemId, quantity: targetQty, productId } = editingCell;
    const prevInventory = inventory; // snapshot for revert-on-error

    // Close the editor now; the entered number shows optimistically in its place.
    setEditingCell(null);
    setSavingId(productId);
    try {
      if (!itemId) {
        // No StoreInventory row yet — create it at the entered count.
        upsertInventoryRow(productId, { quantity: targetQty, needs_reorder: targetQty <= 10 }); // optimistic
        const res = await storeInventoryAPI.create({ product_id: productId, quantity: targetQty });
        upsertInventoryRow(productId, {
          id: res.id,
          quantity: res.quantity ?? targetQty,
          needs_reorder: res.needs_reorder ?? targetQty <= 10,
          reorder_threshold: res.reorder_threshold ?? 10,
        });
      } else {
        const item = inventory.find(i => i.id === itemId);
        if (!item) return;
        const delta = targetQty - item.quantity;
        if (delta === 0) return; // no-op edit — nothing to send
        setInventory(prev => prev.map(r => r.id === itemId ? { ...r, quantity: targetQty } : r)); // optimistic
        const res = await storeInventoryAPI.adjust(itemId, delta, 'COUNT_ADJUST', `Set count to ${targetQty}`);
        setInventory(prev => prev.map(r => r.id === itemId
          ? { ...r, quantity: res.new_quantity, needs_reorder: res.needs_reorder } : r));
      }
      setSuccess('Quantity updated');
      setTimeout(() => setSuccess(''), 2000);
    } catch (err: any) {
      setInventory(prevInventory); // revert the optimistic change
      setError(err.message || 'Failed to save');
    } finally {
      setSavingId(null);
    }
  };

  const handleEditCancel = () => {
    setEditingCell(null);
  };

  if (!isAuthenticated) return null;

  return (
    <div style={styles.page}>
      <StoreHeader />

      <main style={styles.main}>
        <div style={styles.header}>
          <div>
            <h1 style={styles.title}>Store Inventory</h1>
            <p style={styles.subtitle}>Inline edit stock counts</p>
          </div>
          <div style={styles.headerActions}>
            <button
              style={styles.refreshBtn}
              onClick={loadData}
              disabled={loading}
            >
              Refresh
            </button>
          </div>
        </div>

        {/* Filters */}
        <div style={styles.filters}>
          <input
            type="text"
            style={styles.searchInput}
            placeholder="Search name, SKU, or UPC..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />
          <select
            style={styles.filterSelect}
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
          >
            <option value="all">All Categories</option>
            {CATEGORIES.map(cat => (
              <option key={cat.value} value={cat.value}>{cat.label}</option>
            ))}
          </select>

          <label style={styles.checkboxLabel}>
            <input
              type="checkbox"
              checked={showLowOnly}
              onChange={(e) => setShowLowOnly(e.target.checked)}
            />
            Show Low Stock Only
          </label>
        </div>

        {error && <div style={styles.error}>{error}</div>}
        {success && <div style={styles.success}>{success}</div>}

        {loading ? (
          <div style={styles.loading}>Loading inventory...</div>
        ) : (
          <div style={styles.tableContainer}>
            <table style={styles.table}>
              <thead>
                <tr style={styles.headerRow}>
                  <th style={styles.th}>Product Name</th>
                  <th style={styles.th}>Form</th>
                  <th style={styles.th}>Size</th>
                  <th style={styles.th}>SKU</th>
                  <th style={styles.th}>UPC</th>
                  <th style={{...styles.th, textAlign: 'center'}}>In Stock</th>
                  <th style={{...styles.th, textAlign: 'center'}}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(groupedData).map(([category, items]) => (
                  <React.Fragment key={category}>
                    {/* Category Header Row */}
                    <tr style={styles.categoryRow}>
                      <td colSpan={7} style={styles.categoryCell}>
                        {CATEGORIES.find(c => c.value === category)?.label
                          || (!category || category === 'null' ? 'Not Applicable' : category)}
                        <span style={styles.categoryCount}>({items.length} items)</span>
                      </td>
                    </tr>

                    {/* Product Rows */}
                    {items.map((item) => {
                      const isLow = item.needsReorder || item.quantity <= item.reorderThreshold;
                      // Key on product id, not item.id: unstocked rows all share
                      // item.id === 0, so keying on it would edit/spin every zero row.
                      const isEditing = editingCell?.productId === item.product.id;
                      const isSaving = savingId === item.product.id;

                      return (
                        <tr
                          key={item.product.id}
                          style={{
                            ...styles.row,
                            ...(isLow ? styles.lowStockRow : {}),
                            ...(isSaving ? styles.savingRow : {}),
                          }}
                        >
                          <td style={styles.td}>
                            <div style={styles.productName}>{item.product.name}</div>
                            {!item.product.is_active && (
                              <span style={styles.inactiveBadge}>Inactive</span>
                            )}
                          </td>
                          <td style={styles.td}>
                            <span style={{
                              ...styles.formBadge,
                              backgroundColor: item.product.form === 'PELLET' ? '#e3f2fd' : '#fff3e0',
                              color: item.product.form === 'PELLET' ? '#1565c0' : '#e65100',
                            }}>
                              {item.product.form_display}
                            </span>
                          </td>
                          <td style={styles.td}>{item.product.size_lbs}#</td>
                          <td style={styles.tdCode}>{item.product.sku}</td>
                          <td style={styles.tdCode}>{item.product.upc || '-'}</td>
                          <td style={styles.tdQty}>
                            {item.weightTracked ? (
                              <span style={styles.qtyDisplay} title="Pound pool (weight-tracked)">
                                {item.quantityLbs.toFixed(2)} lbs
                              </span>
                            ) : isEditing ? (
                              <input
                                type="text"
                                inputMode="numeric"
                                style={styles.qtyInput}
                                value={editingCell.quantity}
                                onChange={(e) => handleEditChange(e.target.value)}
                                onBlur={() => handleEditSave()}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') handleEditSave();
                                  if (e.key === 'Escape') handleEditCancel();
                                }}
                                autoFocus
                              />
                            ) : (
                              <span
                                style={{
                                  ...styles.qtyDisplay,
                                  ...(isLow ? styles.lowQty : {}),
                                }}
                                onClick={() => handleEditStart(item.id, item.quantity, item.product.id)}
                                title="Click to edit"
                              >
                                {item.quantity}
                              </span>
                            )}
                          </td>
                          <td style={styles.tdActions}>
                            {item.weightTracked ? (
                              <WeightPoolCell
                                itemId={item.id}
                                productId={item.product.id}
                                quantityLbs={item.quantityLbs}
                                onUpdated={(patch) => upsertInventoryRow(item.product.id, {
                                  weight_tracked: true,
                                  quantity_lbs: patch.quantity_lbs,
                                  ...(patch.id ? { id: patch.id } : {}),
                                })}
                                onError={setError}
                              />
                            ) : isEditing ? (
                              <div style={styles.editActions}>
                                <button
                                  style={styles.saveBtn}
                                  onClick={handleEditSave}
                                  disabled={isSaving}
                                >
                                  Save
                                </button>
                                <button
                                  style={styles.cancelBtn}
                                  onClick={handleEditCancel}
                                >
                                  X
                                </button>
                              </div>
                            ) : (
                              <div style={styles.quickActions}>
                                <button
                                  style={styles.minusBtn}
                                  onClick={() => handleQuickAdjust(item.id, -1, item.product.id)}
                                  disabled={isSaving || item.quantity <= 0}
                                  title="Decrease by 1"
                                >
                                  -
                                </button>
                                <button
                                  style={styles.plusBtn}
                                  onClick={() => handleQuickAdjust(item.id, 1, item.product.id)}
                                  disabled={isSaving}
                                  title="Increase by 1"
                                >
                                  +
                                </button>
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </React.Fragment>
                ))}
              </tbody>
            </table>

            {Object.keys(groupedData).length === 0 && (
              <div style={styles.emptyState}>
                No products found matching your filters.
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  page: {
    minHeight: '100vh',
    backgroundColor: '#f5f5f5',
  },
  main: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '1.5rem',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '1.5rem',
  },
  title: {
    fontSize: '1.75rem',
    fontWeight: 'bold',
    color: '#1a4d1a',
    margin: 0,
  },
  subtitle: {
    color: '#666',
    margin: '0.25rem 0 0',
  },
  headerActions: {
    display: 'flex',
    gap: '0.75rem',
  },
  refreshBtn: {
    backgroundColor: '#1a4d1a',
    color: '#fff',
    border: 'none',
    padding: '0.5rem 1rem',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '0.9rem',
  },
  filters: {
    display: 'flex',
    gap: '1rem',
    alignItems: 'center',
    marginBottom: '1rem',
    backgroundColor: '#fff',
    padding: '1rem',
    borderRadius: '8px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
  },
  filterSelect: {
    padding: '0.5rem 1rem',
    fontSize: '0.95rem',
    border: '1px solid #ddd',
    borderRadius: '6px',
    backgroundColor: '#fff',
  },
  searchInput: {
    flex: '1 1 240px',
    minWidth: '200px',
    padding: '0.5rem 1rem',
    fontSize: '0.95rem',
    border: '1px solid #ddd',
    borderRadius: '6px',
  },
  checkboxLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    fontSize: '0.9rem',
    cursor: 'pointer',
  },
  error: {
    backgroundColor: '#ffebee',
    color: '#c62828',
    padding: '1rem',
    borderRadius: '8px',
    marginBottom: '1rem',
  },
  success: {
    backgroundColor: '#e8f5e9',
    color: '#2e7d32',
    padding: '1rem',
    borderRadius: '8px',
    marginBottom: '1rem',
  },
  loading: {
    textAlign: 'center',
    padding: '3rem',
    color: '#666',
    fontSize: '1.1rem',
  },
  tableContainer: {
    backgroundColor: '#fff',
    borderRadius: '8px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    overflow: 'hidden',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '0.9rem',
  },
  headerRow: {
    backgroundColor: '#1a4d1a',
    color: '#fff',
  },
  th: {
    padding: '0.75rem 1rem',
    textAlign: 'left',
    fontWeight: 600,
    fontSize: '0.85rem',
  },
  categoryRow: {
    backgroundColor: '#f5f5f5',
  },
  categoryCell: {
    padding: '0.75rem 1rem',
    fontWeight: 600,
    color: '#333',
    fontSize: '0.95rem',
  },
  categoryCount: {
    marginLeft: '0.5rem',
    color: '#888',
    fontWeight: 400,
    fontSize: '0.85rem',
  },
  row: {
    borderBottom: '1px solid #e0e0e0',
  },
  lowStockRow: {
    backgroundColor: '#fff8e1',
  },
  savingRow: {
    opacity: 0.6,
  },
  td: {
    padding: '0.75rem 1rem',
    verticalAlign: 'middle',
  },
  productName: {
    fontWeight: 500,
    color: '#333',
  },
  inactiveBadge: {
    display: 'inline-block',
    backgroundColor: '#f5f5f5',
    color: '#999',
    padding: '0.15rem 0.5rem',
    borderRadius: '4px',
    fontSize: '0.7rem',
    marginLeft: '0.5rem',
  },
  formBadge: {
    display: 'inline-block',
    padding: '0.2rem 0.5rem',
    borderRadius: '4px',
    fontSize: '0.75rem',
    fontWeight: 500,
  },
  tdCode: {
    padding: '0.75rem 1rem',
    fontFamily: 'monospace',
    fontSize: '0.8rem',
    color: '#555',
  },
  tdQty: {
    padding: '0.5rem 1rem',
    textAlign: 'center',
  },
  qtyDisplay: {
    display: 'inline-block',
    minWidth: '50px',
    padding: '0.35rem 0.75rem',
    backgroundColor: '#f5f5f5',
    borderRadius: '4px',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'background-color 0.2s',
  },
  lowQty: {
    backgroundColor: '#ffcdd2',
    color: '#c62828',
  },
  qtyInput: {
    width: '60px',
    padding: '0.35rem 0.5rem',
    fontSize: '0.9rem',
    textAlign: 'center',
    border: '2px solid #1a4d1a',
    borderRadius: '4px',
  },
  tdActions: {
    padding: '0.5rem 1rem',
    textAlign: 'center',
  },
  quickActions: {
    display: 'flex',
    justifyContent: 'center',
    gap: '0.35rem',
  },
  minusBtn: {
    width: '28px',
    height: '28px',
    backgroundColor: '#ff5252',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '1rem',
    fontWeight: 'bold',
  },
  plusBtn: {
    width: '28px',
    height: '28px',
    backgroundColor: '#4CAF50',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '1rem',
    fontWeight: 'bold',
  },
  editActions: {
    display: 'flex',
    justifyContent: 'center',
    gap: '0.35rem',
  },
  saveBtn: {
    padding: '0.35rem 0.75rem',
    backgroundColor: '#4CAF50',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '0.8rem',
    fontWeight: 500,
  },
  cancelBtn: {
    width: '28px',
    height: '28px',
    backgroundColor: '#f5f5f5',
    color: '#666',
    border: '1px solid #ddd',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '0.8rem',
  },
  emptyState: {
    textAlign: 'center',
    padding: '3rem',
    color: '#888',
    fontSize: '1rem',
  },
};

export default StoreInventoryList;
